import {createRouter, createWebHistory, RouteRecordRaw} from 'vue-router'
import {buildExternalLoginUrl, getAccessToken, getAuthMode} from './services/auth'
import {hardNavigate} from './services/navigation'
import {refreshTokenWithLock, isTokenExpiringSoon} from './services/tokenRefresh'
import type {AccessMode} from './services/user'
import {selectRouteTable, setBuiltAccessMode} from './routeTable'
import {nextRoutes} from './next/router-next'
import {dialerOnlyRoutes} from './next/router-dialer'

import LoginPage from './views/LoginPage.vue'
import CallbackPage from './views/CallbackPage.vue'
import UnauthorizedPage from './views/UnauthorizedPage.vue'
// Auth + error routes — registered in both UI modes because they render
// outside any AppShell and need to work regardless of which UI is active.
const authRoutes: RouteRecordRaw[] = [
  { path: '/login', name: 'login', component: LoginPage },
  { path: '/callback', name: 'callback', component: CallbackPage },
  { path: '/unauthorized', name: 'unauthorized', component: UnauthorizedPage },
]

// The active route table is decided once, at boot, from the resolved access
// mode (main.ts awaits /auth/me when a token exists) — the dialer table IS
// the dialer-only allowlist.
export function buildRouter(accessMode: AccessMode) {
  setBuiltAccessMode(accessMode)
  const kind = selectRouteTable(accessMode)
  const routes: RouteRecordRaw[] =
    kind === 'dialer' ? [...authRoutes, ...dialerOnlyRoutes]
    : [...authRoutes, ...nextRoutes]

  const router = createRouter({
    history: createWebHistory(),
    routes,
  })

  router.beforeEach(async (to) => {
    const token = getAccessToken()
    const authMode = getAuthMode()

    // Allow callback and unauthorized routes without authentication
    if (to.name === 'callback' || to.name === 'unauthorized') {
      return true
    }

    // If token exists but expiring soon, try refresh
    if (authMode === 'external' && token && isTokenExpiringSoon(token)) {
      console.log('[Router] Token expiring soon, attempting refresh before navigation...')
      await refreshTokenWithLock()
    }

    // Re-check token after potential refresh
    const currentToken = getAccessToken()

    // An unmatched URL (e.g. a dialer link visited while the dialer-only
    // table is active, which has no catch-all) carries no requiresAuth
    // meta — without this it would render blank instead of logging in.
    // Authenticated visits fall through and land wherever the table sends
    // unmatched paths.
    const unmatched = to.matched.length === 0

    if ((to.meta.requiresAuth || unmatched) && !currentToken) {
      if (authMode === 'external') {
        const externalLoginUrl = import.meta.env.VITE_EXTERNAL_IDP_LOGIN_URL
        if (externalLoginUrl) {
          // Build full callback URL with return path
          const returnPath = to.fullPath
          // Redirect to external login
          hardNavigate(buildExternalLoginUrl(returnPath))
          return false
        }
      }
      return { name: 'login' }
    }

    if (to.name === 'login' && currentToken) {
      // Path, not { name: 'home' }: the dialer table has no 'home' record, and
      // every table redirects '/' to its own home.
      return { path: '/' }
    }

    return true
  })

  router.afterEach((to) => {
    const pageTitle = to.meta?.title as string | undefined
    document.title = pageTitle ? `${pageTitle} | Raven GTM` : 'Raven GTM'
  })

  return router
}
